import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import {
  attachInstruction,
  extractInstruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { DragData, DropData, DropPosition } from './types';
import { extractDragPayload } from './useDraggableNode';

export const DROP_PAYLOAD_KEY = 'enhancedai/sidebar-drop';

export interface DropPayload {
  drop: DropData;
}

export type DropTargetMode = 'item' | 'folder' | 'root';

interface UseDropTargetNodeArgs {
  data: DropData;
  mode: DropTargetMode;
  disabled?: boolean;
  canDrop?: (drag: DragData) => boolean;
  forceInside?: boolean;
}

export const resolveDropPositionFromTarget = (
  targetData: Record<string | symbol, unknown>,
  dropKind: DropData['kind'],
): DropPosition => {
  if (dropKind === 'root-drop') return 'inside';
  const instruction = extractInstruction(targetData);
  if (instruction) {
    if (instruction.type === 'reorder-above') return 'above';
    if (instruction.type === 'reorder-below') return 'below';
    return 'inside';
  }
  const edge = extractClosestEdge(targetData);
  if (edge === 'top') return 'above';
  if (edge === 'bottom') return 'below';
  return 'inside';
};

export const useDropTargetNode = ({
  data,
  mode,
  disabled,
  canDrop,
  forceInside,
}: UseDropTargetNodeArgs) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isOver, setIsOver] = useState(false);
  const [position, setPosition] = useState<DropPosition | null>(null);
  const dataRef = useRef<DropData>(data);
  const modeRef = useRef<DropTargetMode>(mode);
  const disabledRef = useRef<boolean>(!!disabled);
  const canDropRef = useRef<typeof canDrop>(canDrop);
  const forceInsideRef = useRef<boolean>(!!forceInside);

  useLayoutEffect(() => {
    dataRef.current = data;
    modeRef.current = mode;
    disabledRef.current = !!disabled;
    canDropRef.current = canDrop;
    forceInsideRef.current = !!forceInside;
  }, [data, mode, disabled, canDrop, forceInside]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pragmatic reads via refs; rebind only on element change.
  useEffect(() => {
    if (!element) return;
    const unsubscribe = dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        if (disabledRef.current) return false;
        const payload = extractDragPayload(source.data);
        if (!payload) return false;
        if (payload.drag.spaceId !== dataRef.current.spaceId) return false;
        if (canDropRef.current) return canDropRef.current(payload.drag);
        return true;
      },
      getData: ({ input, element: el, source }) => {
        const base: Record<string | symbol, unknown> = {
          [DROP_PAYLOAD_KEY]: { drop: dataRef.current },
        };
        const currentMode = modeRef.current;
        if (currentMode === 'root') return base;
        if (forceInsideRef.current) return base;
        const payload = extractDragPayload(source.data);
        if (!payload) return base;
        const draggingFolder = payload.drag.kind === 'folder';
        if (currentMode === 'folder') {
          if (!draggingFolder) return base;
          return attachInstruction(base, {
            input,
            element: el,
            currentLevel: 0,
            indentPerLevel: 0,
            mode: 'standard',
          });
        }
        if (draggingFolder) return base;
        return attachClosestEdge(base, {
          input,
          element: el,
          allowedEdges: ['top', 'bottom'],
        });
      },
      onDragEnter: ({ self }) => {
        const kind = dataRef.current.kind;
        setIsOver(true);
        setPosition(resolveDropPositionFromTarget(self.data, kind));
      },
      onDrag: ({ self }) => {
        const kind = dataRef.current.kind;
        setPosition(resolveDropPositionFromTarget(self.data, kind));
      },
      onDragLeave: () => {
        setIsOver(false);
        setPosition(null);
      },
      onDrop: () => {
        setIsOver(false);
        setPosition(null);
      },
    });
    return () => {
      unsubscribe();
      setIsOver(false);
      setPosition(null);
    };
  }, [element]);

  const setRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  return { setRef, isOver, position };
};

export const extractDropPayload = (
  data: Record<string | symbol, unknown>,
): DropPayload | null => {
  const raw = data[DROP_PAYLOAD_KEY];
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as { drop?: unknown };
  if (typeof candidate.drop !== 'object' || candidate.drop === null)
    return null;
  if (typeof (candidate.drop as { kind?: unknown }).kind !== 'string')
    return null;
  return candidate as DropPayload;
};
